import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  CssBaseline
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const colors = [
  { name: "Red", hex: "#e53935" },
  { name: "Blue", hex: "#1e88e5" },
  { name: "Green", hex: "#43a047" },
  { name: "Yellow", hex: "#fdd835" },
  { name: "Purple", hex: "#8e24aa" },
  { name: "Orange", hex: "#fb8c00" }
];

const ColorMatch = () => {
  const [currentColor, setCurrentColor] = useState({});
  const [options, setOptions] = useState([]);
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(100);
  const [isPlaying, setIsPlaying] = useState(false);
  const timerRef = useRef(null);
  const [themeMode, setThemeMode] = useState("light");

  const theme = createTheme({
    palette: {
      mode: themeMode,
      primary: { main: themeMode === "light" ? "#1976d2" : "#90caf9" },
      background: {
        default: themeMode === "light" ? "#f4f7fa" : "#0d1117",
        paper: themeMode === "light" ? "#fff" : "#161b22"
      }
    },
    shape: { borderRadius: 12 }
  });

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startGame = () => {
    setIsPlaying(true);
    setScore(0);
    setTimeLeft(100);
    nextRound();
  };

  const stopGame = () => {
    setIsPlaying(false);
    clearTimer();
  };

  // ✅ FIXED LOGIC — correct color ALWAYS in options
  const nextRound = () => {
    clearTimer();

    const correctColor =
      colors[Math.floor(Math.random() * colors.length)];

    const textColor =
      colors[Math.floor(Math.random() * colors.length)];

    const wrongOptions = colors
      .filter(c => c.name !== correctColor.name)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);

    const finalOptions = [...wrongOptions, correctColor].sort(
      () => Math.random() - 0.5
    );

    setCurrentColor({
      text: textColor.name,
      correct: correctColor.name,
      displayColor: correctColor.hex
    });

    setOptions(finalOptions);
    setTimeLeft(100);
  };

  const handleSelect = (color) => {
    if (!isPlaying) return;

    if (color.name === currentColor.correct) {
      setScore(s => s + 1);
      nextRound();
    } else {
      stopGame();
    }
  };

  useEffect(() => {
    if (!isPlaying) return;

    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          stopGame();
          return 0;
        }
        return t - 1;
      });
    }, 100);

    return clearTimer;
  }, [isPlaying]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background:
            themeMode === "light"
              ? "linear-gradient(135deg,#f0f4ff,#c3d9ff)"
              : "linear-gradient(135deg,#0f2027,#203a43,#2c5364)",
          py: 4
        }}
      >
        <Card sx={{ p: 3, borderRadius: 4, boxShadow: 8, minWidth: 320 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="h5" fontWeight="bold">
                🎨 Color Match
              </Typography>
              <Button
                onClick={() =>
                  setThemeMode(m => (m === "light" ? "dark" : "light"))
                }
              >
                {themeMode === "light" ? "Dark" : "Light"}
              </Button>
            </Box>

            <Box textAlign="center" mt={3}>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{ color: currentColor.displayColor }}
              >
                {currentColor.text || "Tap Start"}
              </Typography>

              <LinearProgress
                variant="determinate"
                value={timeLeft}
                sx={{ mt: 2, height: 8, borderRadius: 4 }}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2,1fr)",
                gap: 2,
                mt: 3
              }}
            >
              {options.map(color => (
                <Button
                  key={color.name}
                  onClick={() => handleSelect(color)}
                  sx={{
                    bgcolor: color.hex,
                    color: "#fff",
                    fontWeight: "bold",
                    fontSize: 16,
                    py: 2,
                    borderRadius: 2,
                    "&:hover": { opacity: 0.8 }
                  }}
                >
                  {color.name}
                </Button>
              ))}
            </Box>

            <Typography align="center" mt={3}>
              Score: {score}
            </Typography>

            <Box display="flex" justifyContent="center" mt={2}>
              {!isPlaying ? (
                <Button variant="contained" onClick={startGame}>
                  Start
                </Button>
              ) : (
                <Button variant="outlined" color="error" onClick={stopGame}>
                  Stop
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  );
};

export default ColorMatch;
